<template>
  <div class="rounded-2xl border border-gray-200 bg-white p-6">
    <div class="flex flex-col gap-5 mb-6 sm:flex-row sm:justify-between">
      <div class="w-full">
        <h3 class="text-lg font-semibold text-gray-800">Statistiques</h3>
        <p class="mt-1 text-gray-500 text-theme-sm">
          Débits et crédit sur le solde principal
        </p>
      </div>

      <div class="relative">
        <div
          class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
        >
          <button
            v-for="option in options"
            :key="option.value"
            @click="selectPeriod(option)"
            :class="[
              selectedPeriod === option.value
                ? 'shadow-theme-xs text-gray-900  bg-white'
                : 'text-gray-500',
              'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
            ]"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="max-w-full overflow-hidden custom-scrollbar">
      <MultiDataFetcher
        :dataKeys="['creditWithdrawStats', 'creditWithdrawStats']"
        :filters="[
          { type: 'credit', period: selectedPeriod },
          { type: 'withdraw', period: selectedPeriod },
        ]"
        v-slot="{ loading }"
        @fetched="
          ({ data }) => {
            const [credit, withdraw] = data
            setStats(credit, withdraw)
            $forceUpdate()
          }
        "
      >
        <div :class="{ shimmerElement: loading }">
          <div class="-ml-4 min-w-full pl-2">
            <VueApexCharts
              type="area"
              height="310"
              :options="chartOptions"
              :series="series"
            />
          </div>
        </div>
      </MultiDataFetcher>
    </div>
  </div>
</template>

<script setup lang="ts">
import MultiDataFetcher from '@/features/data/presentation/components/MultiDataFetcher.vue'
import VueApexCharts from 'vue3-apexcharts'
import { onMounted, ref } from 'vue'
import Commons from '@/shared/utils/commons'

const options = [
  {
    value: 'year',
    label: 'Année',
    periodMarks: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
  },
  {
    value: 'month',
    label: 'Mois',
    periodMarks: Array.from({ length: 31 }, (_, i) => (i + 1).toString()), // Jours du mois 1 à 31
  },
  {
    value: 'week',
    label: 'Semaine',
    periodMarks: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'], // Jours de la semaine
  },
]

const selectedPeriod = ref('week')
const oldStats = ref(null)

const series = ref([
  {
    name: 'Crédits',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Débit',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
])

const chartOptions = ref({
  legend: {
    show: false,
    position: 'top',
    horizontalAlign: 'left',
  },
  colors: ['#FF7200', '#FFA200'],
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'area',
    toolbar: {
      show: false,
    },
  },
  fill: {
    gradient: {
      enabled: true,
      opacityFrom: 0.55,
      opacityTo: 0,
    },
  },
  stroke: {
    curve: 'straight',
    width: [2, 2],
  },
  markers: {
    size: 0,
  },
  labels: {
    show: false,
    position: 'top',
  },
  grid: {
    xaxis: {
      lines: {
        show: false,
      },
    },
    yaxis: {
      lines: {
        show: true,
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    x: {
      format: 'dd MMM yyyy',
    },
  },
  xaxis: {
    type: 'category',
    categories: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  yaxis: {
    title: {
      style: {
        fontSize: '0px',
      },
    },
  },
})

function selectPeriod(option, initial) {
  if (!initial && selectedPeriod.value == option.value) return
  selectedPeriod.value = option.value
  oldStats.value = null

  series.value = series.value.map(el => ({
    ...el,
    data: option.periodMarks.map(() => 60),
  }))

  // Met à jour les catégories de l'axe X
  chartOptions.value = {
    ...chartOptions.value,
    xaxis: { ...chartOptions.value.xaxis, categories: [...option.periodMarks] },
  }
}

function setStats(credit, withdraw) {
  console.log("🚀 ~ setStats ~ credit, withdraw:", credit, withdraw)
  if (!credit?.stats || !withdraw.stats) return
  if (
    Commons.deepEqual(oldStats.value, {
      credit,
      withdraw,
    })
  )
    return
  oldStats.value = {
    credit,
    withdraw,
  }

  series.value = [
    { ...series.value[0], data: credit.stats },
    { ...series.value[1], data: withdraw.stats },
  ]
}
onMounted(() => {
  selectPeriod(options[2], true)
})
</script>

<style scoped>
.area-chart {
  width: 100%;
}
</style>
